import { useState } from 'react';
import { IconoCerrar } from '../../componentes/Iconos';
import { Panel, Vacio } from '../../componentes/UI';
import { useAvisos } from '../../contextos/AvisosContext';
import type { ProyectoDetalle } from '../../lib/consultas';
import { useAgregarNota, useEliminarNota } from '../../lib/mutaciones';

/**
 * Bitácora del proyecto.
 *
 * Es donde queda por escrito lo que no cabe en un campo: por qué se atrasó una
 * entrega, qué pidió el organismo por teléfono, qué se acordó en una reunión.
 * Cuando alguien pregunta seis meses después qué pasó, esto es lo único que lo
 * responde.
 */
export function PestanaBitacora({ proyecto }: { proyecto: ProyectoDetalle }) {
  const [texto, setTexto] = useState('');

  const avisos = useAvisos();
  const agregar = useAgregarNota();
  const eliminar = useEliminarNota();

  const guardar = async () => {
    const limpio = texto.trim();
    if (!limpio) return;

    try {
      await agregar.mutateAsync({ proyectoId: proyecto.id, texto: limpio });
      setTexto('');
    } catch (error) {
      avisos.error(error instanceof Error ? error.message : 'No se pudo guardar la nota.');
    }
  };

  const borrar = async (id: string) => {
    try {
      await eliminar.mutateAsync(id);
      avisos.exito('Nota eliminada');
    } catch (error) {
      avisos.error(error instanceof Error ? error.message : 'No se pudo eliminar la nota.');
    }
  };

  return (
    <Panel titulo="Bitácora" subtitulo="Lo que pasó en el proyecto y no cabe en ningún otro campo">
      <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
        <textarea
          value={texto}
          onChange={(evento) => setTexto(evento.target.value)}
          onKeyDown={(evento) => {
            // Ctrl+Enter guarda: escribir una nota larga y tener que soltar el
            // teclado para hacer clic interrumpe justo cuando estás pensando.
            if ((evento.ctrlKey || evento.metaKey) && evento.key === 'Enter') {
              evento.preventDefault();
              void guardar();
            }
          }}
          placeholder="Ej: el organismo pidió postergar la entrega dos semanas por disponibilidad de sus bodegas."
          rows={3}
          aria-label="Nueva nota"
          style={{
            flex: 1,
            padding: '11px 14px',
            background: 'var(--bg-input)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-sans)',
            fontSize: 13,
            resize: 'vertical',
            lineHeight: 1.5,
          }}
        />
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => void guardar()}
          disabled={texto.trim().length === 0 || agregar.isPending}
          style={{ alignSelf: 'flex-start' }}
        >
          {agregar.isPending ? 'Guardando…' : 'Agregar'}
        </button>
      </div>

      {proyecto.notasBitacora.length === 0 ? (
        <Vacio
          titulo="Sin notas todavía"
          detalle="Anota acuerdos, cambios de alcance y conversaciones. Es el registro que explica por qué el proyecto fue como fue."
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {proyecto.notasBitacora.map((nota) => (
            <div
              key={nota.id}
              style={{
                display: 'flex',
                gap: 14,
                padding: '14px 0',
                borderBottom: '1px solid var(--border-subtle)',
                alignItems: 'flex-start',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>
                  {nota.texto}
                </div>
                <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 6, fontFamily: 'var(--font-mono)' }}>
                  {nota.creadoEn.slice(0, 16).replace('T', ' ')}
                  {nota.autor ? ` · ${nota.autor.nombre}` : ''}
                </div>
              </div>

              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => void borrar(nota.id)}
                disabled={eliminar.isPending}
                style={{ color: 'var(--accent-red)', flexShrink: 0 }}
                aria-label="Eliminar nota"
              >
                <IconoCerrar size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}
